import { memo, useState } from 'react';
import { Form, FormItemProps, message } from 'antd';

import { Shipping, ShippingWay } from 'models/auction/Shipping';
import { AuctionFormField, getAuctionFormErrorMsg } from 'features/auctionFormSlice';

import ShippingField from './ShippingField/ShippingField';
import LinkStyleButton from 'pages/common/components/LinkStyleButton';

interface ShippingFormProps {
  type: ShippingWay;
  label: string;
  insertMessage: string;
  shippingMethods: Shipping[];
  createShippingMethod: (shippingMethod: Shipping) => void;
  updateShippingMethod: (keyDescription: string, newShippingMethod: Shipping) => void;
  removeShippingMethod: (type: ShippingWay, keyDescription: string) => void;
  errors?: AuctionFormField[];
  clearError?: () => void;
}

function ShippingForm({
  type,
  label,
  insertMessage,
  shippingMethods,
  createShippingMethod,
  updateShippingMethod,
  removeShippingMethod,
  errors,
  clearError,
}: ShippingFormProps) {
  const emptyShippingMethod: Shipping = { type, description: '', cost: null };
  const [showInsertField, setShowInsertField] = useState(false);

  const onClickShowInsertField = () => {
    if (showInsertField) {
      message.warning('기존에 작성 중이던 내용을 모두 작성한 뒤 새 항목을 추가해주세요.');
      return;
    }

    clearError?.();
    setShowInsertField(true);
  };

  const closeInsertField = () => {
    setShowInsertField(false);
  };

  const checkDuplicateShippingMethod = (type: ShippingWay, description: string) =>
    shippingMethods
      .filter((shipping) => shipping.type === type)
      .some((shipping) => shipping.description === description);

  const error: FormItemProps = errors
    ? {
        validateStatus: errors.includes('shippingMethods') ? 'error' : '',
        help: errors.includes('shippingMethods') ? getAuctionFormErrorMsg('shippingMethods') : '',
      }
    : {};

  return (
    <Form.Item label={label} {...error}>
      {shippingMethods
        .filter((shipping) => shipping.type === type)
        .map((shippingMethod) => (
          <ShippingField
            key={shippingMethod.description}
            shippingMethod={shippingMethod}
            createShippingMethod={createShippingMethod}
            updateShippingMethod={updateShippingMethod}
            removeShippingMethod={removeShippingMethod}
            checkDuplicateShippingMethod={checkDuplicateShippingMethod}
          />
        ))}
      {showInsertField && (
        <ShippingField
          shippingMethod={emptyShippingMethod}
          createShippingMethod={createShippingMethod}
          updateShippingMethod={updateShippingMethod}
          removeShippingMethod={removeShippingMethod}
          checkDuplicateShippingMethod={checkDuplicateShippingMethod}
          closeInsertField={closeInsertField}
        />
      )}
      <LinkStyleButton text={insertMessage} onClick={onClickShowInsertField} />
    </Form.Item>
  );
}

export default memo(ShippingForm);
